import { t } from "@lingui/macro";
import { RouteProp, useNavigation } from "@react-navigation/native";
import Constants from "expo-constants";
import { useEffect, useRef } from "react";
import { useState } from "react";
import { ActivityIndicator, Platform, SafeAreaView } from "react-native";
import WebView from "react-native-webview";
import type { WebViewNavigation } from "react-native-webview";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CloseIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { WHOOSH_SESSION_STORAGE_KEY } from "@whoosh/relay/src/constants";
import { getNativeSessionKey } from "@whoosh/relay/src/private/nativeSessionPersistence";
import { getNativeApiHost } from "@whoosh/relay/src/utils/getNativeApiHost";

import { HomeTabsNavigatorParamList } from "../../navigators/HomeTabsNavigator/types";

export const WebViewScreen = ({
  route,
}: {
  route: RouteProp<HomeTabsNavigatorParamList, "WebView">;
}) => {
  const { url, title } = route.params;
  const navigation = useNavigation();
  const webViewRef = useRef<WebView>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  const isWeb = Platform.OS === "web";
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [bridgeUrl, setBridgeUrl] = useState<string | null>(isWeb ? url : null);
  const [isLoadingSession, setIsLoadingSession] = useState(!isWeb);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleNavigationStateChange = (event: WebViewNavigation) => {
    setCanGoBack(event.canGoBack);
    setCanGoForward(event.canGoForward);
  };

  const goBack = () => {
    webViewRef.current?.goBack();
  };

  const goForward = () => {
    webViewRef.current?.goForward();
  };

  useEffect(() => {
    if (isWeb) {
      setBridgeUrl(url);
      return;
    }

    let isMounted = true;
    setIsLoadingSession(true);

    const prepareBridgeUrl = async () => {
      try {
        const configDeployEnv =
          Constants?.expoConfig?.extra?.eas?.deployEnv ?? undefined;
        const isLocal = configDeployEnv === "local";
        const isE2E = configDeployEnv === "e2e";
        const configAPIBase = Constants?.expoConfig?.extra?.eas?.apiBase;

        const apiBase = await getNativeApiHost({
          isLocal,
          configAPIBase,
        });

        if (!apiBase) {
          throw new Error("Unable to resolve API host");
        }

        let baseUrl = `${isLocal || isE2E ? "" : "https://"}${apiBase}`;

        if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
          baseUrl = `https://${baseUrl}`;
        }

        const encodedRedirect = encodeURIComponent(url);
        const nextBridgeUrl = `${baseUrl}/webview-bridge?redirect=${encodedRedirect}`;
        const key = (await getNativeSessionKey()) ?? null;

        if (!isMounted) {
          return;
        }

        setSessionKey(key);
        setBridgeUrl(nextBridgeUrl);
        setLoadError(null);
      } catch (error) {
        if (isMounted) {
          setLoadError(
            "We couldn't open that page right now. Please try again shortly."
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingSession(false);
        }
      }
    };

    prepareBridgeUrl();

    return () => {
      isMounted = false;
    };
  }, [isWeb, url]);

  const renderWebViewContent = () => {
    if (isWeb) {
      return (
        <WebView
          source={{ uri: url }}
          ref={webViewRef}
          onNavigationStateChange={handleNavigationStateChange}
          sharedCookiesEnabled
          thirdPartyCookiesEnabled
        />
      );
    }

    if (isLoadingSession) {
      return (
        <Box flex={1} alignItems="center" justifyContent="center">
          <ActivityIndicator size="large" />
        </Box>
      );
    }

    if (!sessionKey || !bridgeUrl) {
      return (
        <Box flex={1} alignItems="center" justifyContent="center" padding={6}>
          <Text variant="mainSmall" textAlign="center">
            {loadError ??
              t`We couldn't open that page right now. Please try again shortly.`}
          </Text>
        </Box>
      );
    }

    return (
      <WebView
        source={{
          uri: bridgeUrl,
          headers: { [WHOOSH_SESSION_STORAGE_KEY]: sessionKey },
        }}
        ref={webViewRef}
        onNavigationStateChange={handleNavigationStateChange}
        sharedCookiesEnabled
        thirdPartyCookiesEnabled
      />
    );
  };

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <HStack
        justifyContent="space-between"
        alignItems="center"
        backgroundColor={"surface-primary"}
        padding={2}
        borderBottomWidth={1}
        borderBottomColor={"surface-dividers"}
      >
        <HStack width={104}>
          <Button
            accessibilityLabel={t`Close button`}
            variant="ghost"
            onPress={() => navigation.goBack()}
            leftIcon={<CloseIcon />}
            size="small"
          />
        </HStack>
        <HStack flex={1} justifyContent="center">
          <Text variant="mainSmallSemiBold" textAlign="center">
            {title}
          </Text>
        </HStack>
        <HStack gap={2} width={104}>
          <Button
            accessibilityLabel={t`Back button`}
            variant="ghost"
            onPress={goBack}
            leftIcon={<ArrowLeftIcon />}
            size="small"
            isDisabled={!canGoBack}
          />
          <Button
            accessibilityLabel={t`Forward button`}
            variant="ghost"
            onPress={goForward}
            leftIcon={<ArrowRightIcon />}
            size="small"
            isDisabled={!canGoForward}
          />
        </HStack>
      </HStack>

      {renderWebViewContent()}
    </SafeAreaView>
  );
};
